<template>
  <div class="page-container">
    <Sidebar />
    <div class="main-content">
      <div class="page-header">
        <h1>数据概览</h1>
      </div>
      
      <!-- 快捷操作区 -->
      <div class="quick-actions">
        <!-- 价格采集卡片 -->
        <div class="action-card">
          <div class="action-header">
            <span class="action-icon">💰</span>
            <span class="action-title">药材价格采集</span>
          </div>
          <div class="action-desc">自动采集时间: 2:00 / 8:00 / 14:00 / 20:00（每6小时）</div>
          <div class="crawl-mode-select">
            <select v-model="crawlMode" class="mode-dropdown">
              <option value="all">全量采集（市场+产地）</option>
              <option value="market">仅市场价格</option>
              <option value="origin">仅产地价格</option>
            </select>
          </div>
          <button class="refresh-btn" @click="triggerCrawl" :disabled="crawlLoading">
            {{ crawlLoading ? `采集中... (${crawlProgress})` : '立即采集' }}
          </button>
          <div class="action-result" v-if="crawlResult">
            <span :class="crawlResult.success ? 'success' : 'error'">{{ crawlResult.message }}</span>
          </div>
        </div>
        
        <!-- 建议反馈卡片 -->
        <div class="action-card suggestions-card">
          <div class="action-header">
            <span class="action-icon">💡</span>
            <span class="action-title">建议反馈</span>
          </div>
          <div class="status-stats">
            <div class="status-stat pending">
              <span class="count">{{ statsData.suggestionsPending || 0 }}</span>
              <span class="label">待处理</span>
            </div>
            <div class="status-stat processed">
              <span class="count">{{ statsData.suggestionsProcessed || 0 }}</span>
              <span class="label">已处理</span>
            </div>
          </div>
          <router-link to="/suggestions" class="view-link">
            查看建议反馈管理 →
          </router-link>
        </div>
        
        <!-- 采销信息审核卡片 -->
        <div class="action-card trading-card">
          <div class="action-header">
            <span class="action-icon">📊</span>
            <span class="action-title">采销信息审核</span>
          </div>
          <div class="status-stats">
            <div class="status-stat pending">
              <span class="count">{{ statsData.tradingPending || 0 }}</span>
              <span class="label">待审核</span>
            </div>
            <div class="status-stat approved">
              <span class="count">{{ statsData.tradingApproved || 0 }}</span>
              <span class="label">已通过</span>
            </div>
            <div class="status-stat rejected">
              <span class="count">{{ statsData.tradingRejected || 0 }}</span>
              <span class="label">已拒绝</span>
            </div>
          </div>
          <router-link to="/trading" class="view-link">
            查看采销信息管理 →
          </router-link>
        </div>

        <!-- 2025版药典卡片 -->
        <div class="action-card pharmacopoeia-card">
          <div class="action-header">
            <span class="action-icon">📖</span>
            <span class="action-title">2025版药典</span>
          </div>
          <div class="status-stats">
            <div class="status-stat">
              <span class="count">{{ statsData.pharmacopoeiaCount || 0 }}</span>
              <span class="label">药材数量</span>
            </div>
          </div>
          <router-link to="/pharmacopoeia-2025" class="view-link">
            查看药典管理 →
          </router-link>
        </div>

        <!-- 涨跌榜更新卡片 -->
        <div class="action-card rankings-card">
          <div class="action-header">
            <span class="action-icon">📈</span>
            <span class="action-title">涨跌榜数据</span>
          </div>
          <div class="action-desc">市场/产地价格涨跌榜（需本地脚本采集）</div>
          <div class="rankings-info" v-if="crawlTime">
            <span class="crawl-time">最后更新: {{ crawlTime }}</span>
          </div>
          <div class="rankings-buttons">
            <button class="refresh-btn small" @click="requestCrawlRankings('market')" :disabled="crawlLoading">
              更新市场涨跌榜
            </button>
            <button class="refresh-btn small" @click="requestCrawlRankings('origin')" :disabled="crawlLoading">
              更新产地涨跌榜
            </button>
          </div>
          <div class="crawl-tip" v-if="crawlTip">
            {{ crawlTip }}
          </div>
        </div>
      </div>
      
      <div class="stats-grid">
        <div class="stat-card" v-for="stat in stats" :key="stat.key">
          <div class="stat-icon">{{ stat.icon }}</div>
          <div class="stat-info">
            <div class="stat-value">{{ stat.value }}</div>
            <div class="stat-label">{{ stat.label }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Sidebar from '../components/Sidebar.vue'
import axios from 'axios'
import { getAdminPassword } from '../utils/api'

export default {
  name: 'Dashboard',
  components: { Sidebar },
  data() {
    return {
      loading: false,
      result: null,
      crawlLoading: false,
      crawlMode: 'all',
      crawlProgress: '',
      crawlResult: null,
      crawlTime: null,
      crawlTip: null,
      statsData: {
        herbs: 0,
        herbProductions: 0,
        gapBases: 0,
        collectionSchemes: 0,
        xianxianCompanies: 0,
        marketPrices: 0,
        users: 0,
        tradingPosts: 0,
        tradingPending: 0,
        tradingApproved: 0,
        tradingRejected: 0,
        suggestionsPending: 0,
        suggestionsProcessed: 0,
        pharmacopoeiaCount: 0
      }
    }
  },
  computed: {
    stats() {
      return [
        { key: 'herbs', label: '药材数量', value: this.statsData.herbs, icon: '🌿' },
        { key: 'herbProductions', label: '药材产量', value: this.statsData.herbProductions, icon: '📦' },
        { key: 'gapBases', label: 'GAP基地', value: this.statsData.gapBases, icon: '🏭' },
        { key: 'collectionSchemes', label: '采收方案', value: this.statsData.collectionSchemes, icon: '📋' },
        { key: 'xianxianCompanies', label: '饮片企业', value: this.statsData.xianxianCompanies, icon: '🏢' },
        { key: 'marketPrices', label: '市场价格', value: this.statsData.marketPrices, icon: '💰' },
        { key: 'users', label: '注册用户', value: this.statsData.users, icon: '👤' },
        { key: 'tradingPosts', label: '采销信息', value: this.statsData.tradingPosts, icon: '📊' }
      ]
    }
  },
  mounted() {
    this.fetchStats()
    this.fetchCrawlTime()
  },
  methods: {
    async fetchStats() {
      try {
        const response = await axios.get('/api/admin/dashboard-stats', {
          headers: { 'x-admin-password': getAdminPassword() }
        })
        if (response.data.code === 200) {
          this.statsData = response.data.data
        }
        try {
          const pharmaResponse = await axios.get('/api/admin/pharmacopoeia-2025?pageSize=1', {
            headers: { 'x-admin-password': getAdminPassword() }
          })
          if (pharmaResponse.data.code === 200) {
            this.statsData.pharmacopoeiaCount = pharmaResponse.data.data.total
          }
        } catch (e) {
          console.error('获取药典数量失败:', e)
        }
      } catch (err) {
        console.error('获取统计数据失败:', err)
      }
    },
    async triggerCrawl() {
      this.crawlLoading = true
      this.crawlProgress = '0%'
      this.crawlResult = null
      try {
        const response = await axios.post('/api/market/crawl', 
          { mode: this.crawlMode }, 
          {
            headers: { 'x-admin-password': getAdminPassword() },
            timeout: 600000
          }
        )
        if (response.data.code === 200) {
          const { marketCount, originCount, herbCount, duration } = response.data.data
          const total = marketCount + originCount
          this.crawlResult = {
            success: true,
            message: `采集完成！药材${herbCount}种，市场${marketCount}条，产地${originCount}条，耗时${duration}`
          }
          this.crawlProgress = ''
          this.fetchStats()
        } else {
          this.crawlResult = { success: false, message: response.data.msg || '采集失败' }
          this.crawlProgress = ''
        }
      } catch (err) {
        console.error('采集失败:', err)
        this.crawlResult = { 
          success: false, 
          message: err.code === 'ECONNABORTED' ? '请求超时，采集可能仍在进行中' : '网络错误' 
        }
        this.crawlProgress = ''
      } finally {
        this.crawlLoading = false
      }
    },
    async requestCrawlRankings(priceType) {
      this.crawlTip = '请在本地执行: node scripts/crawl-rankings.js ' + priceType
      this.crawlLoading = true
      try {
        await axios.post('/api/admin/crawl-rankings', { priceType }, {
          headers: { 'x-admin-password': getAdminPassword() }
        })
        this.crawlTip = `请求已创建！请在项目根目录执行: node scripts/crawl-rankings.js ${priceType}`
        await this.fetchCrawlTime()
      } catch (err) {
        console.error('创建采集请求失败:', err)
        this.crawlTip = '创建请求失败，请直接在本地运行采集脚本'
      } finally {
        this.crawlLoading = false
      }
    },
    async fetchCrawlTime() {
      try {
        const response = await axios.get('/api/admin/crawl-status', {
          headers: { 'x-admin-password': getAdminPassword() }
        })
        if (response.data.code === 200 && response.data.data.length > 0) {
          const latest = response.data.data.find(r => r.status === 'completed')
          if (latest) {
            this.crawlTime = new Date(latest.completed_at).toLocaleString('zh-CN')
          }
        }
      } catch (err) {
        console.error('获取采集时间失败:', err)
      }
    }
  }
}
</script>

<style scoped>
.page-container { display: flex; min-height: 100vh; background: #f5f5f5; }
.main-content { flex: 1; padding: 30px; margin-left: 260px; }
.page-header { margin-bottom: 30px; }
.page-header h1 { font-size: 28px; color: #2c3e50; font-weight: 600; }
.quick-actions { margin-bottom: 30px; display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; }
.action-card { background: white; border-radius: 10px; padding: 20px; box-shadow: 0 2px 10px rgba(0,0,0,0.1); display: flex; flex-direction: column; gap: 12px; }
.action-header { display: flex; align-items: center; gap: 12px; }
.action-icon { font-size: 24px; }
.action-title { font-size: 18px; font-weight: 600; color: #2c3e50; }
.action-desc { font-size: 14px; color: #666; }
.crawl-mode-select { margin-top: 8px; }
.mode-dropdown { width: 100%; padding: 10px 12px; border: 1px solid #ddd; border-radius: 6px; font-size: 14px; color: #333; background: white; cursor: pointer; }
.mode-dropdown:focus { outline: none; border-color: #10b981; }
.refresh-btn { background: linear-gradient(135deg, #10b981, #059669); color: white; border: none; padding: 12px 24px; border-radius: 8px; font-size: 14px; font-weight: 500; cursor: pointer; transition: all 0.2s; }
.refresh-btn:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3); }
.refresh-btn:disabled { background: #9ca3af; cursor: not-allowed; }
.action-result { font-size: 13px; padding-top: 8px; }
.action-result .success { color: #10b981; }
.action-result .error { color: #ef4444; }

/* 状态卡片通用样式 */
.suggestions-card { border-left: 4px solid #8e44ad; }
.trading-card { border-left: 4px solid #f39c12; }
.pharmacopoeia-card { border-left: 4px solid #e74c3c; }
.rankings-card { border-left: 4px solid #3498db; }
.rankings-info { font-size: 13px; color: #666; padding: 8px 0; }
.crawl-time { color: #27ae60; }
.rankings-buttons { display: flex; gap: 10px; }
.refresh-btn.small { padding: 8px 16px; font-size: 13px; }
.crawl-tip { font-size: 12px; color: #f39c12; padding: 8px; background: #fff3cd; border-radius: 4px; margin-top: 8px; }
.status-stats { display: flex; gap: 15px; padding: 10px 0; }
.status-stat { display: flex; flex-direction: column; align-items: center; padding: 10px 15px; border-radius: 8px; background: #f8f9fa; min-width: 80px; }
.status-stat .count { font-size: 24px; font-weight: 700; }
.status-stat .label { font-size: 12px; color: #666; }
.status-stat.pending .count { color: #f39c12; }
.status-stat.processed .count { color: #27ae60; }
.status-stat.approved .count { color: #27ae60; }
.status-stat.rejected .count { color: #e74c3c; }
.view-link { color: #3498db; font-size: 14px; text-decoration: none; padding-top: 8px; }
.view-link:hover { text-decoration: underline; }

.stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; }
.stat-card { background: white; border-radius: 10px; padding: 24px; display: flex; align-items: center; gap: 16px; box-shadow: 0 2px 10px rgba(0,0,0,0.1); transition: transform 0.2s; }
.stat-card:hover { transform: translateY(-2px); }
.stat-icon { font-size: 32px; }
.stat-info { display: flex; flex-direction: column; gap: 4px; }
.stat-value { font-size: 28px; font-weight: 700; color: #2c3e50; }
.stat-label { font-size: 14px; color: #666; }

@media (max-width: 1600px) {
  .quick-actions { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 1400px) {
  .quick-actions { grid-template-columns: 1fr; }
}
@media (max-width: 1200px) {
  .stats-grid { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 768px) {
  .stats-grid { grid-template-columns: 1fr; }
}
</style>